import '../../App.css';
import { useAuth } from '../../AuthContext';
import { useState, useEffect } from 'react';
import React from 'react';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import * as Icon from 'react-feather';
import MainStructure from '../../components/general/MainStructure';
import { toast } from 'react-toastify';

const API = 'https://api.schedpilot.com/wp-json/api/v1/workspaces';

const s = {
  sectionTitle: {
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#7d8fa3',
    marginBottom: 12,
  },
  input: {
    flex: 1,
    padding: '9px 14px',
    border: '1px solid #d1d9e0',
    borderRadius: 8,
    fontSize: '0.875rem',
    color: '#0f172a',
    outline: 'none',
    background: '#f8fafc',
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '9px 16px',
    background: '#84cc16',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    flexShrink: 0,
  },
  btnGhost: {
    display: 'flex',
    alignItems: 'center',
    background: 'none',
    border: '1px solid #d1d9e0',
    borderRadius: 7,
    padding: '5px 10px',
    fontSize: '0.78rem',
    color: '#475569',
    cursor: 'pointer',
  },
  btnDanger: {
    display: 'flex',
    alignItems: 'center',
    background: 'none',
    border: '1px solid #fecaca',
    borderRadius: 7,
    padding: '5px 10px',
    fontSize: '0.78rem',
    color: '#dc2626',
    cursor: 'pointer',
  },
};

function Workspaces() {
  const { jwtToken } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchWorkspaces = async () => {
    setLoading(true);
    try {
      const res = await fetch(API, { credentials: 'include' });
      const data = await res.json();
      if (Array.isArray(data)) setWorkspaces(data);
    } catch {
      toast.error('Failed to load workspaces.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWorkspaces(); }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(API, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (res.ok) {
        setNewName('');
        fetchWorkspaces();
        toast.success('Workspace created.');
      } else {
        toast.error('Failed to create workspace.');
      }
    } catch {
      toast.error('An error occurred.');
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async (id) => {
    if (!editingName.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editingName.trim() }),
      });
      if (res.ok) {
        setEditingId(null);
        fetchWorkspaces();
        toast.success('Workspace updated.');
      } else {
        toast.error('Failed to update workspace.');
      }
    } catch {
      toast.error('An error occurred.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        fetchWorkspaces();
        toast.success('Workspace deleted.');
      } else {
        toast.error('Failed to delete workspace.');
      }
    } catch {
      toast.error('An error occurred.');
    }
  };

  return (
    <MainStructure activeItem="workspaces">
      <div className="container-fluid px-0">

        {/* ── Header ───────────────────────────────────────────── */}
        <div className="mb-4">
          <h1 style={{ fontSize: '1.3rem', fontWeight: 500, color: '#0f172a', margin: 0 }}>
            Workspaces
          </h1>
          <p style={{ fontSize: '0.85rem', color: '#7d8fa3', margin: 0 }}>
            Organise your social accounts and posts into separate workspaces
          </p>
        </div>

        {/* ── Add new ──────────────────────────────────────────── */}
        <p style={s.sectionTitle}>New workspace</p>
        <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
          <input
            style={s.input}
            placeholder="Workspace name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            onFocus={e => e.target.style.borderColor = '#84cc16'}
            onBlur={e => e.target.style.borderColor = '#d1d9e0'}
          />
          <button className="sp-btn-primary" style={s.btnPrimary} onClick={handleAdd} disabled={adding || !newName.trim()}>
            <Icon.Plus size={15} />
            {adding ? 'Creating…' : 'Create'}
          </button>
        </div>

        {/* ── List ─────────────────────────────────────────────── */}
        <p style={s.sectionTitle}>Your workspaces</p>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div style={{ background: '#fff', border: '1px solid #d1d9e0', borderRadius: 12, overflow: 'hidden' }}>
            {workspaces.map((ws, i) => {
              const isDefault = ws.id === 0;
              return (
                <div
                  key={ws.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '14px 16px',
                    borderBottom: i < workspaces.length - 1 ? '1px solid #f1f5f9' : 'none',
                    gap: 12,
                    background: isDefault ? '#f8fafc' : '#fff',
                  }}
                >
                  {/* Icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 9,
                    background: isDefault ? '#e0f2fe' : '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {isDefault
                      ? <Icon.Home size={16} color="#0ea5e9" />
                      : <Icon.Layers size={16} color="#7d8fa3" />
                    }
                  </div>

                  {/* Name / edit input */}
                  {editingId === ws.id ? (
                    <input
                      style={{ ...s.input, flex: 1 }}
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleSaveEdit(ws.id);
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      onFocus={e => e.target.style.borderColor = '#84cc16'}
                      onBlur={e => e.target.style.borderColor = '#d1d9e0'}
                      autoFocus
                    />
                  ) : (
                    <span style={{ fontSize: '0.9rem', color: '#334155', fontWeight: 500, flexGrow: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {ws.name}
                      {isDefault && (
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#0ea5e9', background: '#e0f2fe', padding: '2px 8px', borderRadius: 10 }}>
                          Default
                        </span>
                      )}
                    </span>
                  )}

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {isDefault ? (
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon.Lock size={12} /> Built-in
                      </span>
                    ) : editingId === ws.id ? (
                      <>
                        <button className="sp-btn-primary" style={s.btnPrimary} onClick={() => handleSaveEdit(ws.id)} disabled={saving}>
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                        <button className="sp-btn-ghost" style={s.btnGhost} onClick={() => setEditingId(null)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="sp-btn-ghost" style={s.btnGhost}
                          onClick={() => { setEditingId(ws.id); setEditingName(ws.name); }}
                        >
                          <Icon.Edit2 size={13} style={{ marginRight: 4 }} /> Rename
                        </button>
                        <button className="sp-btn-danger" style={s.btnDanger} onClick={() => handleDelete(ws.id)}>
                          <Icon.Trash2 size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MainStructure>
  );
}

export default Workspaces;
